/**
 * Kandidatenliste von Schritt 4: laden, nachladen solange der Import läuft, Änderungen speichern.
 * Alle Regeln (Aktiv, Blockade, manuelle Einstufung) setzt das Backend durch; hier wird nur
 * angezeigt, optimistisch umgeschaltet und bei Fehlern zurückgerollt.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  blacklistCandidate,
  getCandidates,
  getWizardBlacklists,
  patchCandidate,
  loadMoreCandidates,
  rejudgeCandidate,
  setContactSex,
  startSearch,
  unblacklistCandidate,
  WizardApiError,
} from './campaign-wizard.api';
import {
  CandidateFilter,
  Judgement,
  WizardBlacklistList,
  WizardCandidate,
  WizardCandidateList,
  WizardState,
} from './campaign-wizard.types';
import {
  needsPolling,
  pollIntervalMs,
  countActive,
  startFailureOf,
  mirrorBlacklistAdd,
  mirrorBlacklistRemove,
  withActive,
} from './steps/candidate-list/candidate-list.logic';

export const CANDIDATE_POLL_MS = 5000;

const EMPTY_LIST: WizardCandidateList = {
  items: [],
  counts: { all: 0, other: 0, sex: 0, blocked: 0 },
  total: 0,
  activeCount: 0,
  importing: false,
  searchEstimate: null,
};

export interface UseWizardCandidates {
  list: WizardCandidateList;
  loading: boolean;
  error: string | null;
  actionError: string | null;
  filter: CandidateFilter;
  setFilter: (filter: CandidateFilter) => void;
  /** Suche (erneut) starten, z. B. nach einem Fehler; lädt die Liste danach neu. */
  restartSearch: () => Promise<void>;
  restarting: boolean;
  /** „Nächste 50 laden“: weitere Profile über die angehaltenen Suchen. */
  loadMore: () => Promise<void>;
  loadingMore: boolean;
  restartError: string | null;
  /** „Suche starten“ lief, aber der Server hat die Suche nicht angelegt (z. B. Sitzung ungültig): Grund. */
  startFailure: string | null;
  /** Alle Blacklists des Mandanten (für die Menü-Ziele im Drawer). */
  blacklists: WizardBlacklistList[];
  refresh: () => Promise<void>;
  setActive: (candidate: WizardCandidate, active: boolean) => Promise<void>;
  setJudgementOf: (candidate: WizardCandidate, judgement: Judgement | null) => Promise<boolean>;
  rejudge: (candidate: WizardCandidate) => Promise<boolean>;
  /** Unklares Geschlecht festlegen; danach zählt der Kandidat wieder nach seiner Bewertung. */
  setSex: (candidate: WizardCandidate, sex: 'female' | 'male') => Promise<boolean>;
  addToBlacklist: (
    candidate: WizardCandidate,
    scope: 'person' | 'company',
    target: string
  ) => Promise<boolean>;
  removeFromBlacklist: (
    candidate: WizardCandidate,
    scope: 'person' | 'company'
  ) => Promise<boolean>;
}

interface Params {
  campaignId: string | null;
  state: WizardState;
  patchState: (partial: Partial<WizardState>) => void;
  /** Grund eines fehlgeschlagenen Suchstarts aus dem Wizard (z. B. beim Weiter aus Schritt 3). */
  initialStartFailure?: string | null;
  /** Meldet das Ergebnis von „Suche (erneut) starten“ an den Wizard (für die Prüfen-Seite). */
  onStartFailure?: (failure: string | null) => void;
}

export const useWizardCandidates = ({
  campaignId,
  state,
  patchState,
  initialStartFailure = null,
  onStartFailure,
}: Params): UseWizardCandidates => {
  const [list, setList] = useState<WizardCandidateList>(EMPTY_LIST);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CandidateFilter>('all');
  const [restarting, setRestarting] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [startFailure, setStartFailure] = useState<string | null>(initialStartFailure);
  const [blacklists, setBlacklists] = useState<WizardBlacklistList[]>([]);
  const stateRef = useRef(state);
  stateRef.current = state;
  const requestId = useRef(0);

  const refresh = useCallback(async () => {
    if (!campaignId) return;
    const id = ++requestId.current;
    try {
      const next = await getCandidates(campaignId, { filter });
      if (id !== requestId.current) return;
      setList(next);
      setError(null);
    } catch (e) {
      if (id === requestId.current) setError((e as WizardApiError).message);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [campaignId, filter]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  // Nur solange die Suche wirklich lädt oder Bewertungen laufen, regelmäßig nachladen (nicht bei
  // „pausiert am Ladeziel“ oder fertiger Suche).
  const polling = needsPolling(list);
  const pollMs = pollIntervalMs(list, CANDIDATE_POLL_MS);
  useEffect(() => {
    if (!polling) return undefined;
    const timer = setInterval(() => void refresh(), pollMs);
    return () => clearInterval(timer);
  }, [polling, pollMs, refresh]);

  const restartSearch = useCallback(async () => {
    if (!campaignId) return;
    setRestarting(true);
    setRestartError(null);
    setStartFailure(null);
    try {
      const res = await startSearch(campaignId);
      const failure = startFailureOf(res);
      setStartFailure(failure);
      onStartFailure?.(failure);
      await refresh();
    } catch (e) {
      setRestartError((e as WizardApiError).message);
    } finally {
      setRestarting(false);
    }
  }, [campaignId, refresh, onStartFailure]);

  const loadMore = useCallback(async () => {
    if (!campaignId) return;
    setLoadingMore(true);
    setActionError(null);
    try {
      const res = await loadMoreCandidates(campaignId);
      if (!res.started && res.message) setActionError(res.message);
      await refresh();
    } catch (e) {
      setActionError((e as WizardApiError).message);
    } finally {
      setLoadingMore(false);
    }
  }, [campaignId, refresh]);

  useEffect(() => {
    getWizardBlacklists()
      .then(res => setBlacklists(res.lists))
      .catch(() => setBlacklists([]));
  }, []);

  const fail = useCallback((e: unknown) => {
    setActionError((e as WizardApiError).message);
  }, []);

  const setActive = useCallback(
    async (candidate: WizardCandidate, active: boolean) => {
      if (!campaignId) return;
      const before = list;
      setActionError(null);
      setList(prev => {
        const items = withActive(prev.items, candidate.id, active);
        return { ...prev, items, activeCount: countActive(items) };
      });
      try {
        await patchCandidate(campaignId, candidate.id, { active });
        await refresh();
      } catch (e) {
        setList(before);
        fail(e);
      }
    },
    [campaignId, fail, list, refresh]
  );

  const setJudgementOf = useCallback(
    async (candidate: WizardCandidate, next: Judgement | null) => {
      if (!campaignId) return false;
      setActionError(null);
      try {
        await patchCandidate(campaignId, candidate.id, { judgement: next });
        await refresh();
        return true;
      } catch (e) {
        fail(e);
        return false;
      }
    },
    [campaignId, fail, refresh]
  );

  const rejudge = useCallback(
    async (candidate: WizardCandidate) => {
      if (!campaignId) return false;
      setActionError(null);
      try {
        await rejudgeCandidate(campaignId, candidate.id);
        await refresh();
        return true;
      } catch (e) {
        fail(e);
        return false;
      }
    },
    [campaignId, fail, refresh]
  );

  const setSex = useCallback(
    async (candidate: WizardCandidate, sex: 'female' | 'male') => {
      setActionError(null);
      try {
        await setContactSex(candidate.id, sex);
        await refresh();
        return true;
      } catch (e) {
        fail(e);
        return false;
      }
    },
    [fail, refresh]
  );

  const addToBlacklist = useCallback(
    async (candidate: WizardCandidate, scope: 'person' | 'company', target: string) => {
      if (!campaignId) return false;
      setActionError(null);
      try {
        await blacklistCandidate(campaignId, candidate.id, scope, target);
        patchState(mirrorBlacklistAdd(stateRef.current, candidate, scope, target));
        await refresh();
        return true;
      } catch (e) {
        fail(e);
        return false;
      }
    },
    [campaignId, fail, patchState, refresh]
  );

  const removeFromBlacklist = useCallback(
    async (candidate: WizardCandidate, scope: 'person' | 'company') => {
      if (!campaignId) return false;
      setActionError(null);
      try {
        await unblacklistCandidate(campaignId, candidate.id, scope);
        patchState(mirrorBlacklistRemove(stateRef.current, candidate, scope));
        await refresh();
        return true;
      } catch (e) {
        fail(e);
        return false;
      }
    },
    [campaignId, fail, patchState, refresh]
  );

  return useMemo(
    () => ({
      list,
      loading,
      error,
      actionError,
      filter,
      setFilter,
      restartSearch,
      restarting,
      loadMore,
      loadingMore,
      restartError,
      startFailure,
      blacklists,
      refresh,
      setActive,
      setJudgementOf,
      rejudge,
      setSex,
      addToBlacklist,
      removeFromBlacklist,
    }),
    [
      list,
      loading,
      error,
      actionError,
      filter,
      restartSearch,
      restarting,
      loadMore,
      loadingMore,
      restartError,
      startFailure,
      blacklists,
      refresh,
      setActive,
      setJudgementOf,
      rejudge,
      setSex,
      addToBlacklist,
      removeFromBlacklist,
    ]
  );
};
